import {useCallback, useSyncExternalStore} from "react";
import {notificationErrorMessage} from "../api/cabinetNotifications";
import type {CabinetNotificationsApi} from "../api/cabinetNotifications";
import {useAuth} from "../auth";
import {useMerchantQuery} from "./useMerchantQuery";

// Счётчик непрочитанных общий для меню и страницы «Уведомления»: меню спрашивает
// сервер раз в 30 секунд (только на видимой вкладке), а страница, получив ленту
// или отметив её прочитанной, кладёт новое число сюда же — без второго запроса.

const POLL_INTERVAL_MS = 30_000;
const BADGE_CAP = 99;

let unreadCount: number | null = null;
const listeners = new Set<() => void>();

/** Положить свежий счётчик; null — «ещё не знаем» (после выхода из кабинета). */
export function publishNotificationUnread(next: number | null): void {
  if (unreadCount === next) return;
  unreadCount = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Текст на значке: пусто при нуле, «99+» от ста. */
export function formatUnreadBadge(count: number | null): string | null {
  if (count === null || count <= 0) return null;
  return count > BADGE_CAP ? `${BADGE_CAP}+` : String(count);
}

/** Текущий общий счётчик без собственных запросов. */
export function useNotificationUnreadValue(): number | null {
  return useSyncExternalStore(subscribe, () => unreadCount, () => null);
}

/**
 * Счётчик для меню кабинета. `paused` отключает свой опрос — пока открыта
 * страница уведомлений, число приносит её собственная лента.
 */
export function useNotificationUnreadCount(list: CabinetNotificationsApi["list"], paused = false): number | null {
  const {token} = useAuth();
  const request = useCallback(
    (signal: AbortSignal) =>
      list(token, {limit: 1}, signal).then((page) => {
        publishNotificationUnread(page.unreadCount);
        return page;
      }),
    [token, list],
  );
  useMerchantQuery(request, `notifications-unread:${token ?? ""}`, {
    enabled: Boolean(token) && !paused,
    pollIntervalMs: POLL_INTERVAL_MS,
    formatError: notificationErrorMessage,
  });
  return useNotificationUnreadValue();
}
